import { get, writable } from "svelte/store";
import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import VisibilityState from "../Map/Enums/VisibilityState";
import { Paint } from "../Map/MapLayerStyles";
import MapStore from "../Map/Store";
import ESRIClassStore from "../Stores/ESRIClassStore";
import NOTAM from "./NOTAM";
import NOTAMActiveState from "./NOTAMActiveState";

import AuthState from "../Auth/Enums/AuthState";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapLayerManager from "../Map/MapLayerManager";
import OAuthStore from "../OAuth/Store";

const NOTAMStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		opacity: writable(1.0),
		visible: writable(false),
		lastUpdated: writable(0),
		dataLayerName: "notams"
	},
	results: {
		notams: writable([]),
		notamsFiltered: writable([]),
		by: {
			// id: writable([]),
			globalId: writable([])
			// id2: writable([]),
			// location: writable([])
		},
		currentGroup: writable({}) // currently opened in details panel
	},
	filters: {
		filterString: writable(""),
		activeState: {
			[NOTAMActiveState.PAST]: writable(true),
			[NOTAMActiveState.CURRENT]: writable(true),
			[NOTAMActiveState.FUTURE]: writable(true)
		}
	},
	actions: {
		init: () => {
			if (get(NOTAMStore.meta.loadState) !== LoadState.INIT) {
				return;
			}

			NOTAMStore.meta.loadState.set(LoadState.LOADING);

			ESRIClassStore.actions.load()
				.then((classes) => {
					const Query = ESRIClassStore.classes.Query.classPrototype;
					const FeatureLayer = ESRIClassStore.classes.FeatureLayer.classPrototype;
					const IdentityManager = ESRIClassStore.classes.IdentityManager.classPrototype;
					const SpatialReference = ESRIClassStore.classes.SpatialReference.classPrototype;

					const layer = new FeatureLayer({
						url: "https://services1.arcgis.com/ZURaVO35aTK1Ugmq/arcgis/rest/services/LIVE_NOTAMS_Polygons/FeatureServer"
					});

					const query = new Query();
					query.outFields = ["*"];
					query.returnGeometry = true;
					query.geometryPrecision = 3;
					query.where = "1 = 1";
					query.outSpatialReference = SpatialReference.WGS84;

					Promise.all([layer.queryFeatures(query)])
						.then((responses) => {
							const response = responses[0];
							const all = [];
							for (let i = 0; i < response.features.length; i++) {
								const n = new NOTAM(response.features[i]);
								all.push(n);
							}
							all.sort((notam1, notam2) => {
								return (notam1.country <= notam2.country) ? -1 : 1;
							});
							NOTAMStore.results.notams.set(all);
							NOTAMStore.results.notamsFiltered.set(all);

							const grouped = NOTAMStore.helpers.groupByProperty(all, "globalId");
							NOTAMStore.results.by.globalId.set(grouped);

							// Plot on map
							const features = all.map((item) => {
								return item.asFeature();
							});
							const map = MapStore.helpers.getMap();
							map.addSource(NOTAMStore.meta.dataLayerName, {
								type: "geojson",
								data: GeoHelper.emptyGeoJson(features)
							});
							map.addLayer(
								{
									id: NOTAMStore.meta.dataLayerName,
									type: "fill",
									source: NOTAMStore.meta.dataLayerName,
									paint: Paint.notam
								},
								"waterway-label"
							);
							MapLayerManager.addLayerGroup(MapLayerGroup.NOTAMS, [NOTAMStore.meta.dataLayerName], [NOTAMStore.meta.dataLayerName]);
							NOTAMStore.meta.loadState.set(LoadState.LOADED);
							NOTAMStore.actions.updateFilters();
						})
						.catch((error) => {
							console.error(error);
							OAuthStore.configs.agol.authState.set(AuthState.LOGGED_OUT);
							NOTAMStore.meta.loadState.set(LoadState.INIT);
						});
				})
				.catch((error) => {
					console.error(error);
					OAuthStore.configs.agol.authState.set(AuthState.LOGGED_OUT);
					NOTAMStore.meta.loadState.set(LoadState.INIT);
				});
		},
		updateFilters: () => {
			if (get(NOTAMStore.meta.loadState) !== LoadState.LOADED) {
				return;
			}

			const notams = get(NOTAMStore.results.notams);
			const showPast = get(NOTAMStore.filters.activeState[NOTAMActiveState.PAST]);
			const showCurr = get(NOTAMStore.filters.activeState[NOTAMActiveState.CURRENT]);
			const showFutr = get(NOTAMStore.filters.activeState[NOTAMActiveState.FUTURE]);

			const filtered = [];
			for (let i = 0; i < notams.length; i++) {
				const notam = notams[i];
				notam.visible = false;
				if ((notam.activeState === NOTAMActiveState.PAST && showPast) ||
					(notam.activeState === NOTAMActiveState.CURRENT && showCurr) ||
					(notam.activeState === NOTAMActiveState.FUTURE && showFutr)) {
					filtered.push(notam);
					notam.visible = true;
				}
			}
			const map = MapStore.helpers.getMap();
			const features = filtered.map((item) => {
				return item.asFeature();
			});
			map.getSource(NOTAMStore.meta.dataLayerName).setData(GeoHelper.emptyGeoJson(features));

			NOTAMStore.results.notamsFiltered.set(filtered);
			NOTAMStore.meta.lastUpdated.set(Date.now());
		},
		updateVisibility: () => {
			const map = MapStore.helpers.getMap();
			if (map) {
				if (get(NOTAMStore.meta.loadState) !== LoadState.LOADED) {
					return;
				}
				const visible = get(NOTAMStore.meta.visible);
				map.setLayoutProperty(NOTAMStore.meta.dataLayerName, "visibility", (visible) ? VisibilityState.VISIBLE : VisibilityState.NONE);
			}
		}
	},
	helpers: {
		findByLocation: (locationCode) => {
			return NOTAMStore.helpers.findByProperty("location", locationCode);
		},
		findByID2: (id2) => {
			return NOTAMStore.helpers.findByProperty("id2", id2);
		},
		findByProperty: (property, value) => {
			const index = get(NOTAMStore.results.by[property]).findIndex((item) => {
				return item.properties[property] === value;
			});
			if (index !== -1) {
				return get(NOTAMStore.results.by[property])[index];
			}
			return null;
		},
		groupByID2: (notams = []) => {
			return NOTAMStore.helpers.groupByProperty(notams, "id2");
		},
		groupByLocation: (notams = []) => {
			return NOTAMStore.helpers.groupByProperty(notams, "location");
		},
		// TODO: consider moving to a generalised placed
		groupByProperty: (notams = [], property = "id2") => {
			const reducedIntoProperties = [];
			const foundProperties = [];
			for (let i = 0; i < notams.length; i++) {
				if (foundProperties.indexOf(notams[i][property]) === -1) {
					const containerFeature = {
						type: "Feature",
						properties: {
							[property]: notams[i][property]
						},
						geometry: {
							...notams[i].data.geometry
						}
					};

					if (containerFeature !== null) {
						reducedIntoProperties.push(containerFeature);
						foundProperties.push(notams[i][property]);

						containerFeature.properties.notams = [notams[i]];
					}
				}
				else {
					const index = foundProperties.indexOf(notams[i][property]);
					reducedIntoProperties[index].properties.notams.push(notams[i]);
				}
			}
			return reducedIntoProperties;
		}

	}
};
export default NOTAMStore;
